import type { EndpointDef } from '@skynet/data-helpers';

/**
 * Central endpoint definitions — single source of truth for the Signup API.
 *
 * This file drives:
 * - **Enhanced API**: computed endpoint keys in `enhanceEndpoints`
 * - **Hook re-exports**: clean name → generated name mapping
 * - **MSW mock factories**: HTTP method + URL path pattern
 *
 * When the OpenAPI spec changes and codegen regenerates, update this
 * file to keep everything in sync.
 */

/**
 * Endpoint definitions keyed by clean export name.
 *
 * Each entry maps a human-friendly name to:
 * - `generated` — the codegen-produced RTK Query endpoint name, derived from the OpenAPI operationId
 * - `method` — HTTP verb
 * - `path` — URL pattern (MSW-compatible `:param` syntax)
 */
export const SIGNUP_API_ENDPOINTS = {
  register: {
    generated: 'localControllerRegisterV1',
    method: 'post',
    path: '/api/v1/auth/register',
  },
  login: {
    generated: 'localControllerLoginV1',
    method: 'post',
    path: '/api/v1/auth/login',
  },
  logout: {
    generated: 'authControllerLogoutV1',
    method: 'post',
    path: '/api/v1/auth/logout',
  },
  revokeTokens: {
    generated: 'authControllerRevokeTokensV1',
    method: 'post',
    path: '/api/v1/auth/revoke-tokens',
  },
  refreshToken: {
    generated: 'jwtRefreshControllerRefreshTokenV1',
    method: 'post',
    path: '/api/v1/auth/refresh-token',
  },
  confirmEmail: {
    generated: 'emailConfirmationControllerConfirmEmailV1',
    method: 'post',
    path: '/api/v1/email/confirm-email',
  },
  resendConfirmationEmail: {
    generated: 'emailConfirmationControllerResendConfirmationLinkV1',
    method: 'post',
    path: '/api/v1/email/resend-confirmation-link',
  },
  generateMfaQr: {
    generated: 'mfaControllerGenerateQrV1',
    method: 'post',
    path: '/api/v1/mfa/generate',
  },
  verifyMfa: {
    generated: 'mfaControllerEnableMfaV1',
    method: 'post',
    path: '/api/v1/mfa/verify',
  },
  authenticateMfa: {
    generated: 'mfaControllerAuthenticateMfaV1',
    method: 'post',
    path: '/api/v1/mfa/authenticate',
  },
  resetMfa: {
    generated: 'mfaControllerResetMfaV1',
    method: 'post',
    path: '/api/v1/mfa/reset',
  },
  recoverMfa: {
    generated: 'mfaRecoveryCodeControllerRecoverMfaV1',
    method: 'post',
    path: '/api/v1/mfa/recover',
  },
  requestPasswordReset: {
    generated: 'resetPasswordControllerSendResetPasswordEmailV1',
    method: 'post',
    path: '/api/v1/password',
  },
  resetPassword: {
    generated: 'resetPasswordControllerResetPasswordV1',
    method: 'put',
    path: '/api/v1/password',
  },
  getPasswordResetSession: {
    generated: 'resetPasswordControllerGetResetPasswordSessionV1',
    method: 'get',
    path: '/api/v1/password/session',
  },
  verifyPasswordResetEmail: {
    generated: 'resetPasswordControllerVerifyResetPasswordEmailV1',
    method: 'post',
    path: '/api/v1/password/verify-email',
  },
  verifyPasswordReset2fa: {
    generated: 'resetPasswordControllerVerifyResetPasswordMfaV1',
    method: 'post',
    path: '/api/v1/password/verify-2fa',
  },
  verifyPasswordResetRecoveryCode: {
    generated: 'resetPasswordControllerVerifyResetPasswordRecoveryCodeV1',
    method: 'post',
    path: '/api/v1/password/verify-recovery-code',
  },
} as const satisfies Record<string, EndpointDef>;

export type SignupEndpointName = keyof typeof SIGNUP_API_ENDPOINTS;
